import type { ObjectId } from 'mongodb';

import { ChecklistEvaluationKey } from './ChecklistEvaluations';

/** Slate.js document node — stored as JSON in the DB. */
export interface RichTextNode {
  type?: string;
  children?: RichTextNode[];
  text?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  url?: string;
  [key: string]: unknown;
}
import type { EvaluationParams, EvaluationState, EvaluationView } from './ChecklistEvaluations';
import type { AllDocumentTypes } from './DocumentTypes';
import type { Meta } from './Entities';
import { FlagPurposeType, FlagReason } from './Flags';
import { ProgramType } from './ProgramTypes';
import { QueueType } from './Queues';

export enum ChecklistItemStatus {
  pending = 'pending',
  inProgress = 'inProgress',
  completed = 'completed',
  skipped = 'skipped',
}

export enum ChecklistStatus {
  pending = 'pending',
  inProgress = 'inProgress',
  completed = 'completed',
  archived = 'archived',
}

/**
 * All `ChecklistStatus` values except `archived`. MongoDB partial index filter expressions don't
 * support `$ne`/`$not` — only equality, `$in`, `$exists: true`, range operators, `$type`, and
 * top-level `$and` — so excluding archived checklists from the `checklists_queueId_unique` index
 * has to be expressed as this enumerated `$in` set instead.
 */
export const NON_ARCHIVED_CHECKLIST_STATUSES: ReadonlyArray<ChecklistStatus> = [
  ChecklistStatus.pending,
  ChecklistStatus.inProgress,
  ChecklistStatus.completed,
];

export enum ChecklistConditionName {
  battery = 'battery',
  state = 'state',
  financeType = 'financeType',
  programType = 'programType',
  licensedOrganization = 'licensedOrganization',
  utility = 'utility',
  panelManufacturer = 'panelManufacturer',
  panelModel = 'panelModel',
  inverterManufacturer = 'inverterManufacturer',
  batteryManufacturer = 'batteryManufacturer',
  mountingManufacturer = 'mountingManufacturer',
  domesticContent = 'domesticContent',
  designPlatform = 'designPlatform',
  batteryBackupType = 'batteryBackupType',
  existingSystem = 'existingSystem',
}

export enum ChecklistConditionOperator {
  Is = 'is',
  IsNot = 'isNot',
  StartsWith = 'startsWith',
  Includes = 'includes',
  EndsWith = 'endsWith',
}

export const OPERATORLESS_CONDITIONS: Set<ChecklistConditionName> = new Set<ChecklistConditionName>([
  ChecklistConditionName.domesticContent,
  ChecklistConditionName.batteryBackupType,
  ChecklistConditionName.existingSystem,
]);

export const CASE_INSENSITIVE_CONDITIONS: Set<ChecklistConditionName> = new Set<ChecklistConditionName>([
  ChecklistConditionName.designPlatform,
]);

export const ParameterizedConditions: Set<ChecklistConditionName> = new Set<ChecklistConditionName>([
  ChecklistConditionName.battery,
  ChecklistConditionName.state,
  ChecklistConditionName.financeType,
  ChecklistConditionName.programType,
  ChecklistConditionName.licensedOrganization,
  ChecklistConditionName.utility,
  ChecklistConditionName.panelManufacturer,
  ChecklistConditionName.panelModel,
  ChecklistConditionName.inverterManufacturer,
  ChecklistConditionName.batteryManufacturer,
  ChecklistConditionName.mountingManufacturer,
  ChecklistConditionName.designPlatform,
  ChecklistConditionName.batteryBackupType,
]);

export interface ChecklistCondition {
  name: ChecklistConditionName;
  operator?: ChecklistConditionOperator;
  params?: string[];
}

export enum ChecklistItemInputType {
  text = 'text',
  requirement = 'requirement',
  referenceValue = 'referenceValue',
  evaluation = 'evaluation',
}

export type ChecklistDecisionType = FlagPurposeType | 'approvedNoFlag' | 'approved';

export const ChecklistDecisionTypeLabels: Record<ChecklistDecisionType, string> = {
  approvedNoFlag: 'Approved - Not Applicable',
  approved: 'Approved',
  [FlagPurposeType.stipulation]: 'Stipulation',
  [FlagPurposeType.rejection]: 'Rejection',
  [FlagPurposeType.additionalReview]: 'Additional Review',
  [FlagPurposeType.notification]: 'Notification',
  [FlagPurposeType.changeOrder]: 'Change Order',
};

export enum ChecklistReferenceKey {
  addressLine1 = 'addressLine1',
  addressLine2 = 'addressLine2',
  city = 'city',
  coApplicantFullName = 'coApplicantFullName',
  customerFullAddress = 'customerFullAddress',
  customerFullName = 'customerFullName',
  designTool = 'designTool',
  firstYearProductionKwh = 'firstYearProductionKwh',
  inverterManufacturer = 'inverterManufacturer',
  isBatteryOnContract = 'isBatteryOnContract',
  monitoringId = 'monitoringId',
  mountingType = 'mountingType',
  solarMonthlyPayment = 'solarMonthlyPayment',
  state = 'state',
  systemSizeKw = 'systemSizeKw',
  totalMonthlyPayment = 'totalMonthlyPayment',
  utilityBillMonthlyCharges = 'utilityBillMonthlyCharges',
  utilityName = 'utilityName',
  zip = 'zip',
  contractSystemSizeKw = 'contractSystemSizeKw',
  contractFirstYearProductionKwh = 'contractFirstYearProductionKwh',
  contractElectricalAdder = 'contractElectricalAdder',
  contractType = 'contractType',
  panelManufacturer = 'panelManufacturer',
  panelModel = 'panelModel',
  panelCount = 'panelCount',
  inverterModel = 'inverterModel',
  inverterCount = 'inverterCount',
  storageManufacturer = 'storageManufacturer',
  storageModel = 'storageModel',
  storageCount = 'storageCount',
  mountingManufacturer = 'mountingManufacturer',
  mountingModel = 'mountingModel',
  asBuiltSystemSizeKw = 'asBuiltSystemSizeKw',
  utilityBillUtilityName = 'utilityBillUtilityName',
  utilityBillAccountNumber = 'utilityBillAccountNumber',
  utilityBillMeterNumber = 'utilityBillMeterNumber',
  utilityBillCustomerName = 'utilityBillCustomerName',
  utilityBillAddress = 'utilityBillAddress',
  utilityBillTariff = 'utilityBillTariff',
  tariffId = 'tariffId',
  plansetSystemSizeKwDc = 'plansetSystemSizeKwDc',
  plansetSystemSizeKwAc = 'plansetSystemSizeKwAc',
  plansetCustomerName = 'plansetCustomerName',
  plansetAddress = 'plansetAddress',
  plansetPvModule = 'plansetPvModule',
  plansetInverter = 'plansetInverter',
  plansetBattery = 'plansetBattery',
  plansetUtilityName = 'plansetUtilityName',
  plansetAhj = 'plansetAhj',
  shadeReportDesignPlatform = 'shadeReportDesignPlatform',
  utilityBillAmountOverdue = 'utilityBillAmountOverdue',
}

export const ChecklistReferenceKeyLabels: Record<ChecklistReferenceKey, string> = {
  [ChecklistReferenceKey.addressLine1]: 'Address Line 1',
  [ChecklistReferenceKey.addressLine2]: 'Address Line 2',
  [ChecklistReferenceKey.city]: 'City',
  [ChecklistReferenceKey.coApplicantFullName]: 'Co-Applicant Full Name',
  [ChecklistReferenceKey.customerFullAddress]: 'Customer Full Address',
  [ChecklistReferenceKey.customerFullName]: 'Customer Full Name',
  [ChecklistReferenceKey.designTool]: 'Design Platform',
  [ChecklistReferenceKey.firstYearProductionKwh]: 'First Year Production kWh (System Design)',
  [ChecklistReferenceKey.inverterManufacturer]: 'Inverter Manufacturer (System Design)',
  [ChecklistReferenceKey.isBatteryOnContract]: 'Battery on Contract (Quote)',
  [ChecklistReferenceKey.monitoringId]: 'Monitoring ID',
  [ChecklistReferenceKey.mountingType]: 'Mounting Type (System Design)',
  [ChecklistReferenceKey.solarMonthlyPayment]: 'Solar Monthly Payment (Quote)',
  [ChecklistReferenceKey.state]: 'State',
  [ChecklistReferenceKey.systemSizeKw]: 'System Size kW (System Design)',
  [ChecklistReferenceKey.totalMonthlyPayment]: 'Total Monthly Payment (Quote)',
  [ChecklistReferenceKey.utilityBillMonthlyCharges]: 'Utility Bill Monthly Charges (Utility Bill)',
  [ChecklistReferenceKey.utilityName]: 'Utility Name',
  [ChecklistReferenceKey.zip]: 'Zipcode',
  [ChecklistReferenceKey.contractSystemSizeKw]: 'System Size kW (Contract)',
  [ChecklistReferenceKey.contractFirstYearProductionKwh]: 'First Year Production kWh (Contract)',
  [ChecklistReferenceKey.contractElectricalAdder]: 'Electrical Adder (Contract)',
  [ChecklistReferenceKey.contractType]: 'Contract Type (Contract)',
  [ChecklistReferenceKey.panelManufacturer]: 'Panel Manufacturer (System Design)',
  [ChecklistReferenceKey.panelModel]: 'Panel Model (System Design)',
  [ChecklistReferenceKey.panelCount]: 'Panel Count (System Design)',
  [ChecklistReferenceKey.inverterModel]: 'Inverter Model (System Design)',
  [ChecklistReferenceKey.inverterCount]: 'Inverter Count (System Design)',
  [ChecklistReferenceKey.storageManufacturer]: 'Storage Manufacturer (System Design)',
  [ChecklistReferenceKey.storageModel]: 'Storage Model (System Design)',
  [ChecklistReferenceKey.storageCount]: 'Storage Count (System Design)',
  [ChecklistReferenceKey.mountingManufacturer]: 'Mounting Manufacturer (System Design)',
  [ChecklistReferenceKey.mountingModel]: 'Mounting Model (System Design)',
  [ChecklistReferenceKey.asBuiltSystemSizeKw]: 'As-Built System Size kW (System Design)',
  [ChecklistReferenceKey.utilityBillUtilityName]: 'Utility Name (Utility Bill)',
  [ChecklistReferenceKey.utilityBillAccountNumber]: 'Utility Account Number (Utility Bill)',
  [ChecklistReferenceKey.utilityBillMeterNumber]: 'Meter Number (Utility Bill)',
  [ChecklistReferenceKey.utilityBillCustomerName]: 'Customer Name (Utility Bill)',
  [ChecklistReferenceKey.utilityBillAddress]: 'Customer Address (Utility Bill)',
  [ChecklistReferenceKey.utilityBillTariff]: 'Tariff (Utility Bill)',
  [ChecklistReferenceKey.tariffId]: 'Tariff ID',
  [ChecklistReferenceKey.plansetSystemSizeKwDc]: 'System Size kW DC (Planset)',
  [ChecklistReferenceKey.plansetSystemSizeKwAc]: 'System Size kW AC (Planset)',
  [ChecklistReferenceKey.plansetCustomerName]: 'Customer Name (Planset)',
  [ChecklistReferenceKey.plansetAddress]: 'Customer Address (Planset)',
  [ChecklistReferenceKey.plansetPvModule]: 'PV Module (Planset)',
  [ChecklistReferenceKey.plansetInverter]: 'Inverter (Planset)',
  [ChecklistReferenceKey.plansetBattery]: 'Battery (Planset)',
  [ChecklistReferenceKey.plansetUtilityName]: 'Utility Name (Planset)',
  [ChecklistReferenceKey.plansetAhj]: 'AHJ (Planset)',
  [ChecklistReferenceKey.shadeReportDesignPlatform]: 'Design Platform (Shade Report)',
  [ChecklistReferenceKey.utilityBillAmountOverdue]: 'Amount Overdue (Utility Bill)',
};

/** A path in the flag hierarchy (Flag Requirement > Flag > Issue)*/
export interface ChecklistFlagMapping {
  requirementFlagSettingId: string;
  criterionId?: string;
  problemId?: string;
}

export interface ChecklistItemInputOption {
  label: string;
  value: string;
  decisionType?: ChecklistDecisionType;
  /** Derived by the builder from `flagMappings[0]`; `flagMappings` will be the source of
   *  truth in further cards
   */
  requirementFlagSettingId?: string;
  flagMappings?: ChecklistFlagMapping[];
}

export interface ChecklistPendingFlagDecision {
  optionValue: string;
  purposeType: FlagPurposeType;
  requirementFlagSettingId?: string;
  note?: string;
  selectedReason?: FlagReason;
  selectedCriterionId?: string;
  selectedProblems?: { label: string; value: string }[];
}

export interface ChecklistItemInputDefinition {
  key: string;
  label: string;
  type: ChecklistItemInputType;
  required?: boolean;
  options?: ChecklistItemInputOption[];
  referenceKey?: ChecklistReferenceKey;
  requirementFlagSettingId?: string;
  submittedValue?: string | boolean;
  submittedAt?: Date;
  submittedBy?: string;
  conditions?: ChecklistCondition[];
  tooltipText?: RichTextNode[];
  evaluationKey?: ChecklistEvaluationKey;
  evaluationParams?: EvaluationParams;
}

export interface ChecklistItemInputDefinitionView extends ChecklistItemInputDefinition {
  resolvedValue?: string;
  evaluation?: EvaluationView;
}

export interface ChecklistItemView extends Omit<ChecklistItem, 'inputs'> {
  inputs?: ChecklistItemInputDefinitionView[];
}

export interface ChecklistDocumentView extends Omit<ChecklistDocument, 'items'> {
  items: ChecklistItemView[];
}

export interface ChecklistTemplateSection {
  key: string;
  title: string;
  order: number;
}

export interface ChecklistTemplateItem {
  key: string;
  name: string;
  description: string;
  tooltipText?: RichTextNode[];
  order: number;
  sectionKey?: string;
  conditions?: ChecklistCondition[];
  inputs?: ChecklistItemInputDefinition[];
  documentTypes?: AllDocumentTypes[];
}

export enum ChecklistTemplateStatus {
  draft = 'draft',
  published = 'published',
  archived = 'archived',
}

export interface ChecklistTemplateDocument {
  id: ObjectId;
  name: string;
  description: string;
  programTypes: ProgramType[];
  queueType?: QueueType;
  templateGroupId: ObjectId;
  version: number;
  isCurrent: boolean;
  status?: ChecklistTemplateStatus;
  draftNotes?: string;
  sections?: ChecklistTemplateSection[];
  items: ChecklistTemplateItem[];
  meta: Meta;
}

export interface SaveChecklistTemplateDraftRequest {
  name?: string;
  description?: string;
  programTypes?: ProgramType[];
  items?: ChecklistTemplateItem[];
  sections?: ChecklistTemplateSection[];
  draftNotes?: string;
}

export interface PublishChecklistTemplateDraftRequest {
  draftNotes: string;
}

export interface ChecklistItem {
  key: string;
  name: string;
  description: string;
  tooltipText?: RichTextNode[];
  order: number;
  sectionKey?: string;
  status: ChecklistItemStatus;
  completedBy?: string;
  completedAt?: Date;
  aiCompleted?: boolean;
  inputs?: ChecklistItemInputDefinition[];
  documentTypes?: AllDocumentTypes[];
  data?: Record<string, string | boolean | ChecklistPendingFlagDecision[]>;
  evaluations?: Record<string, EvaluationState>;
}

export interface ChecklistHistoryEntry {
  itemKey: string;
  previousStatus: ChecklistItemStatus;
  status: ChecklistItemStatus;
  changedBy: string;
  changedAt: Date;
  notes?: string;
  aiCompleted?: boolean;
}

export interface ChecklistDocument {
  id: ObjectId;
  queueId: ObjectId;
  templateId: ObjectId;
  templateName: string;
  templateVersion: number;
  templateGroupId: ObjectId;
  accountId: ObjectId;
  sections?: ChecklistTemplateSection[];
  items: ChecklistItem[];
  history: ChecklistHistoryEntry[];
  status: ChecklistStatus;
  completedAt?: Date;
  completedBy?: string;
  meta: Meta;
}

export const ChecklistItemStatusLabels: Record<ChecklistItemStatus, string> = {
  [ChecklistItemStatus.pending]: 'Pending',
  [ChecklistItemStatus.inProgress]: 'In Progress',
  [ChecklistItemStatus.completed]: 'Completed',
  [ChecklistItemStatus.skipped]: 'Skipped',
};

export const ChecklistStatusLabels: Record<ChecklistStatus, string> = {
  [ChecklistStatus.pending]: 'Pending',
  [ChecklistStatus.inProgress]: 'In Progress',
  [ChecklistStatus.completed]: 'Completed',
  [ChecklistStatus.archived]: 'Archived',
};

export const ChecklistConditionNameLabels: Record<ChecklistConditionName, string> = {
  [ChecklistConditionName.battery]: 'Battery',
  [ChecklistConditionName.state]: 'State',
  [ChecklistConditionName.financeType]: 'Finance Type',
  [ChecklistConditionName.programType]: 'Program Type',
  [ChecklistConditionName.licensedOrganization]: 'Licensed Organization',
  [ChecklistConditionName.utility]: 'Utility',
  [ChecklistConditionName.panelManufacturer]: 'Panel Manufacturer',
  [ChecklistConditionName.panelModel]: 'Panel Model',
  [ChecklistConditionName.inverterManufacturer]: 'Inverter Manufacturer',
  [ChecklistConditionName.batteryManufacturer]: 'Battery Manufacturer',
  [ChecklistConditionName.mountingManufacturer]: 'Mounting Manufacturer',
  [ChecklistConditionName.domesticContent]: 'Domestic Content',
  [ChecklistConditionName.designPlatform]: 'Design Platform',
  [ChecklistConditionName.batteryBackupType]: 'Battery Backup Type',
  [ChecklistConditionName.existingSystem]: 'Existing System',
};
